/**
 * Enrollment tokens, in Settings' Endpoints group: the tokens endpoints
 * register with. Backed by:
 *   GET    /v1/provisioning-tokens          → list
 *   POST   /v1/provisioning-tokens          → create
 *   DELETE /v1/provisioning-tokens/:jwt_id  → revoke
 *
 * A count and the column picker over the table. Tokens are created in Enroll
 * endpoints (ConnectPage), which shows the new token with the install
 * command; this list only reads and revokes them.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { restGet, restDelete } from "../lib/restClient.js";
import OverflowTooltip from "../components/OverflowTooltip.jsx";
import DataTable from "../components/DataTable.jsx";
import ColumnPicker from "../components/ColumnPicker.jsx";
import ConfirmDialog from "../components/ConfirmDialog.jsx";
import { useDataTableColumns } from "../lib/columnSelection.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { denialCopy, denialOrRawMessage } from "../lib/denialReason.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";
import Button from "../components/ui/Button.jsx";
import Listing from "../components/ui/Listing.jsx";
import { Count } from "../components/ui/TableBar.jsx";
import { formatCompact } from "../lib/renderers.js";
import { timeLabelFormatter, dayLabelFormatter } from "../lib/traceUtils.js";

// ── Schema ───────────────────────────────────────────────────────

const COLUMNS = [
    // On a narrow screen the columns that matter least fold away first
    // (`fold`, 1 first): Token ID, then who made it, then when.
    { id: "status",         label: "Status",       width: 100 },
    { id: "createdAt",      label: "Created",      width: 120, fold: 3 },
    { id: "expiresAt",      label: "Expires",      width: 120 },
    { id: "usage",          label: "Used",         width: 80, fold: 4 },
    { id: "createdByEmail", label: "Created by",   width: 200, fold: 2 },
    { id: "jwtId",          label: "Token ID",     width: 200, flex: true, minWidth: 140, fold: 1 },
    { id: "_actions",       label: "",             width: 84 },
];

export function getProvisioningTokenColumns(canManageTokens) {
    return canManageTokens ? COLUMNS : COLUMNS.filter((column) => column.id !== "_actions");
}

export function provisioningTokenEmptyState(canManageTokens, error = null) {
    if (error) return { title: "Token list unavailable", note: error };
    return {
        title: "No enrollment tokens",
        note: canManageTokens
            ? "Generate one in Enroll endpoints. An endpoint needs one to register with Origin."
            : "No enrollment tokens have been created for this tenant.",
    };
}

const PAGE_SIZE = 1000;

// ── Row shape ────────────────────────────────────────────────────

function flattenToken(t) {
    const created = t.createdAt ?? t.created_at ?? null;
    const expires = t.expiresAt ?? t.expires_at ?? null;
    const revokedAt = t.revokedAt ?? t.revoked_at ?? null;
    const createdDate = created ? new Date(created) : null;
    const expiresDate = expires ? new Date(expires) : null;
    const revoked = Boolean(t.revoked);
    const expired = expiresDate ? expiresDate.getTime() < Date.now() : false;
    const status = revoked ? "revoked" : expired ? "expired" : "active";
    return {
        jwtId:               t.jwtId  ?? t.jwt_id  ?? "",
        createdAt:           Number.isFinite(createdDate?.getTime?.()) ? createdDate : null,
        expiresAt:           Number.isFinite(expiresDate?.getTime?.()) ? expiresDate : null,
        revokedAt:           revokedAt ? new Date(revokedAt) : null,
        maxUses:             Number(t.maxUses ?? t.max_uses ?? 0),
        endpointsRegistered: Number(t.endpointsRegistered ?? t.endpoints_registered ?? 0),
        createdByEmail:      t.createdByEmail ?? t.created_by_email ?? "",
        revoked,
        status, // "active" | "expired" | "revoked"
    };
}

// ── Cell content ─────────────────────────────────────────────────

const STATUS_DOT = {
    active:  "bg-[color:var(--green)]",
    expired: "bg-[color:var(--muted-soft)]",
    revoked: "bg-[color:var(--danger)]",
};

function StatusCell({ status }) {
    return (
        <span className="inline-flex items-center gap-[6px] text-[12.5px] leading-[1.2]">
            <span className={`h-[7px] w-[7px] flex-none rounded-full ${STATUS_DOT[status] ?? "bg-[color:var(--muted-soft)]"}`} />
            <span className="capitalize">{status}</span>
        </span>
    );
}

function TimestampCell({ value }) {
    if (!(value instanceof Date)) {
        return <span className="text-[color:var(--muted-soft)]">—</span>;
    }
    return (
        <div className="grid gap-[1px]">
            <span className="text-[12.5px] leading-[1.2] text-[color:var(--ink)]">
                {timeLabelFormatter.format(value)}
            </span>
            <span className="text-[11.5px] leading-[1.2] text-[color:var(--muted-soft)]">
                {dayLabelFormatter.format(value)}
            </span>
        </div>
    );
}

function UsageCell({ used, max }) {
    const exhausted = max > 0 && used >= max;
    return (
        <span className={`text-[12.5px] leading-[1.2] tabular-nums ${exhausted ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}>
            <span>{used}</span>
            <span className="text-[color:var(--muted-soft)]"> / {max || "∞"}</span>
        </span>
    );
}

// ── Page ─────────────────────────────────────────────────────────

// `onCreate` opens where tokens are made (Enroll endpoints' Set up), for the
// empty state.
export default function ProvisioningTokensPage({ onCreate = null }) {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(null);
    const [page, setPage] = useState(0);
    // Bumped by Retry to read the current page again.
    const [attempt, setAttempt] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Per-row revoke state. Map<jwtId, "pending" | error>.
    const [revoking, setRevoking] = useState({});
    // The token waiting for the reader to confirm its revocation.
    const [confirmRevoke, setConfirmRevoke] = useState(null);

    // Revoke is a management action. Hide (don't disable) it
    // for a reader who can list tokens but can't manage them — the page
    // stays useful as a read-only inventory. Fails open pre-RBAC.
    const caps = useMyPermissions();
    const canManageTokens = hasPermission(caps, PERMISSIONS.PROVISIONING_TOKENS_MANAGE);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const resp = await restGet("/v1/provisioning-tokens", { page, page_size: PAGE_SIZE });
                if (cancelled) return;
                const list = (resp?.tokens ?? []).map(flattenToken);
                setRows((prev) => (page === 0 ? list : [...prev, ...list]));
                setTotal(resp?.total ?? list.length);
            } catch (err) {
                if (cancelled) return;
                setError(denialCopy(err, caps).message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [page, caps, attempt]);

    const handleRevoke = useCallback(async (jwtId) => {
        setConfirmRevoke(null);
        setRevoking((m) => ({ ...m, [jwtId]: "pending" }));
        try {
            await restDelete(`/v1/provisioning-tokens/${encodeURIComponent(jwtId)}`);
            // Mark revoked in-place; saves a full refetch.
            setRows((prev) =>
                prev.map((r) =>
                    r.jwtId === jwtId
                        ? { ...r, revoked: true, status: "revoked", revokedAt: new Date() }
                        : r,
                ),
            );
            setRevoking((m) => {
                const { [jwtId]: _, ...rest } = m;
                return rest;
            });
        } catch (err) {
            console.error("[provisioning tokens] revoke", err);
            setRevoking((m) => ({ ...m, [jwtId]: denialOrRawMessage(err, caps) }));
        }
    }, [caps]);

    const tableColumns = useMemo(
        () => getProvisioningTokenColumns(canManageTokens),
        [canManageTokens],
    );
    const cols = useDataTableColumns({
        storageKey: "provisioningTokens",
        columns: tableColumns,
        defaultVisible: tableColumns.map((c) => c.id),
    });
    const emptyState = provisioningTokenEmptyState(canManageTokens);

    const renderCell = useCallback(
        (r, col) => {
            switch (col.id) {
                case "status":
                    return <StatusCell status={r.status} />;
                case "createdAt":
                    return <TimestampCell value={r.createdAt} />;
                case "expiresAt":
                    return <TimestampCell value={r.expiresAt} />;
                case "usage":
                    return <UsageCell used={r.endpointsRegistered} max={r.maxUses} />;
                case "createdByEmail":
                    return (
                        <OverflowTooltip
                            as="span"
                            text={r.createdByEmail || "—"}
                            className="text-[12.5px] leading-[1.2] text-[color:var(--ink)]"
                        />
                    );
                case "jwtId":
                    return (
                        <OverflowTooltip
                            as="span"
                            text={r.jwtId}
                            className="font-mono text-[12px] leading-[1.2] text-[color:var(--muted)]"
                        />
                    );
                case "_actions": {
                    // Hide the revoke affordance entirely without manage.
                    if (!canManageTokens) return null;
                    const revokeState = revoking[r.jwtId];
                    if (revokeState === "pending") {
                        return <span className="text-[12px] text-[color:var(--muted)]">Revoking…</span>;
                    }
                    if (revokeState) {
                        return (
                            <span className="text-[12px] text-[color:var(--danger)]" title={revokeState}>
                                Failed
                            </span>
                        );
                    }
                    if (r.status === "active") {
                        return (
                            <Button variant="danger-ghost" size="sm" className="justify-self-start" onClick={() => setConfirmRevoke(r)}>
                                Revoke
                            </Button>
                        );
                    }
                    return null;
                }
                default:
                    return null;
            }
        },
        [revoking, canManageTokens],
    );


    return (
        <div className="flex min-w-0 flex-col">
            <Listing
                count={total == null ? null : rows.length}
                error={error}
                errorTitle="Token list unavailable"
                onRetry={() => setAttempt((n) => n + 1)}
                retrying={loading}
                // No response yet: Count holds its place rather than read "0".
                bar={<Count n={loading && total == null ? null : total ?? rows.length} one="token" />}
                barEnd={<ColumnPicker {...cols.pickerProps} />}
                empty={{
                    ...emptyState,
                    action: canManageTokens && onCreate ? <Button variant="primary" size="sm" onClick={onCreate}>Create a token</Button> : null,
                }}
            >
                <DataTable
                    cols={cols}
                    rows={rows}
                    getRowId={(r) => r.jwtId}
                    renderCell={renderCell}
                    loading={loading}
                    skeletonRows={4}
                    estimatedRowHeight={40}
                    footer={
                        total != null && rows.length < total ? (
                            <div className="grid place-items-center pb-[10px] pt-[8px]">
                                <Button size="sm" onClick={() => setPage((p) => p + 1)} disabled={loading}>
                                    Load {formatCompact(total - rows.length)} more
                                </Button>
                            </div>
                        ) : null
                    }
                />
            </Listing>

            <ConfirmDialog
                open={confirmRevoke != null}
                analyticsId={DIALOG.TOKEN_REVOKE}
                eyebrow="Enrollment token"
                title="Revoke this token?"
                body={confirmRevoke ? (
                    <div className="grid gap-[8px] text-[13px] leading-[1.5] text-[color:var(--muted)]">
                        <p className="m-0">No endpoint can enroll with this token after you revoke it. You can't undo this.</p>
                        <p className="m-0 break-all font-mono text-[12px] text-[color:var(--ink)]">{confirmRevoke.jwtId}</p>
                    </div>
                ) : null}
                confirmLabel="Revoke token"
                destructive
                onConfirm={() => confirmRevoke && handleRevoke(confirmRevoke.jwtId)}
                onCancel={() => setConfirmRevoke(null)}
            />
        </div>
    );
}
